<template>
  <Modal>
    <div class="modal-header">
      <h5>{{getModalTitle()}}</h5>
      <button
        type="button"
        class="btn btn-link btm-sm btn-close-container"
        @click.stop="onCloseModal">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="modal-body no-overflow">
      <div class="error-container" v-if="step === 3">
        <EmptyMessage v-if="showDuplicateKeyError">
          <h3>มีลิงก์ซ้ำในระบบ กรุณาใช้ลิงก์อื่น</h3>
          <button
            type="button"
            @click="() => { step = 2 } "
            class="btn btn-light btn-lg mt-4">
            กลับไปแก้ไข
          </button>
          <span
            slot="code"
            class="code">
            Code : {{saveCollectionErrorCode || 'not found' }}
          </span>
        </EmptyMessage>
        <EmptyMessage v-if="!showDuplicateKeyError">
          <h3>เกิดข้อผิดพลาดขณะบันทึก</h3>
          <router-link
            :to="`/collections`"
            @click.native="onCloseModal"
            class="btn btn-light btn-lg mt-4">
            กลับสู่หน้าคอลเลคชั่น
          </router-link>
          <span
            slot="code"
            class="code">
            Code : {{saveCollectionErrorCode || 'not found' }}
          </span>
        </EmptyMessage>
      </div>
      <div v-if="step === 1">
        <div class="box-container">
          <div
            class="box"
            :class="{ selected: isSelected('dynamic') }"
            @click="selectMethod('dynamic')">
            <div class="box-content">
              <div class="header"><i class="fas fa-sort mr-2"></i>ไดนามิก</div>
              <div class="subtitle">เลือก{{ title }}โดยใช้แท็ก</div>
            </div>
          </div>
          <div
            class="box"
            :class="{ selected: isSelected('standard') }"
            @click="selectMethod('standard')">
            <div class="box-content">
              <div class="header">
                <i class="fas fa-pencil-alt mr-2"></i>มาตรฐาน
              </div>
              <div class="subtitle">เลือก{{ title }}เอง</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="step === 2">
        <FormGroup v-if="!collection.type">
          <h5 class="name">ประเภท</h5>
          <SelectOptionObject
            :disabledDeleteButton="true"
            :value="
              collectionType.find((option) => option.value === collection.type)
            "
            :options="collectionType"
            :label="'title'"
            :placeholder="'เลือกประเภท'"
            @onChanged="onSelectCollectionType"/>
        </FormGroup>
        <FormGroup :errorMessage="saveCollectionStateError.title">
          <h5 class="name">ชื่อ</h5>
          <input
            type="text"
            placeholder="ชื่อ"
            class="form-control"
            :class="{ 'is-invalid': saveCollectionStateError.title }"
            :value="collection.title"
            @input="updateField($event, 'title')"
            @blur="checkRequired($event, 'title', 'ระบุชื่อ')"/>
          <div class="invalid-feedback" v-if="saveCollectionStateError.title">
            {{ saveCollectionStateError.title }}
          </div>
        </FormGroup>
        <FormGroup :errorMessage="saveCollectionStateError.key">
          <h5 class="name">ลิงก์</h5>
          <small class="help-text">กรุณากรอกเป็นภาษาอังกฤษเท่านั้น</small>
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text">{{`${baseURL}/playlists/`}}</span>
            </div>
            <input
              type="text"
              placeholder="คีย์"
              class="form-control"
              :class="{ 'is-invalid': saveCollectionStateError.key }"
              :value="collection.key"
              @input="updateField($event, 'key')"
              @blur="checkRequired($event, 'key', 'ระบุคีย์')"/>
            <div class="invalid-feedback" v-if="saveCollectionStateError.key">
              {{ saveCollectionStateError.key }}
            </div>
          </div>
        </FormGroup>
        <FormGroup v-if="selectedMethod === 'dynamic'" class="with-help-text">
          <h5 class="name">แท็ก</h5>
          <small class="help-text">{{getCollectionTitle(collection)}}ที่ใช้แท็กนี้จะถูกนำเข้าโดยอัตโนมัติ</small>
          <MultiSelect
            :value="tagValue"
            :isMultiple="true"
            :removeable="true"
            :options="tagOptions"
            :customLabel="'name'"
            :placeholder="'เลือกแท็กที่ต้องการเพิ่ม หรือค้นหาด้วยชื่อแท็ก'"
            @onChanged="addTag"
            @onSearch="debounceSearch"/>
        </FormGroup>
        <FormGroup :errorMessage="saveCollectionStateError.programId" v-if="collection.type === 'video'">
          <h5 class="name">รายการ (ถ้ามี)</h5>
          <SelectOptionObject
            :value="collection.program"
            :options="programs"
            :label="'title'"
            :placeholder="'เลือกรายการ'"
            :class="{'is-invalid': saveCollectionStateError.programId }"
            :removeable="false"
            @onChanged="onSelectedProgram"
            @onSearch="loadPrograms" />
          <div
            class="invalid-feedback"
            v-if="saveCollectionStateError.programId">
            กรุณาเลือกรายการ
          </div>
        </FormGroup>
        <FormGroup :errorMessage="saveCollectionStateError.contentCategory" v-if="collection.type === 'video'">
          <h5 class="name">หมวดหมู่คอนเทนท์</h5>
          <SelectOptionObject
            :class="{'is-invalid': saveCollectionStateError.contentCategory }"
            :value="collection.contentCategory"
            :disabledDeleteButton="true"
            :allowEmpty="false"
            :options="contentCategories"
            :disabled="hasContentCategory()"
            :multiple="true"
            @onChanged="onSelectedContentCategory" />
          <div
            class="invalid-feedback"
            v-if="saveCollectionStateError.contentCategory">
            {{saveCollectionStateError.contentCategory}}
          </div>
        </FormGroup>
      </div>
    </div>
    <div class="modal-footer" v-if="step !== 3">
      <button
        v-if="step === 1"
        @click="gotoNextStep()"
        :disabled="!selectedMethod"
        type="submit"
        class="btn btn-primary">
        ต่อไป <i class="fas fa-chevron-right ml-2"></i>
      </button>
      <button
        v-if="step === 2"
        @click="onSubmit"
        :disabled="!isFormValid()"
        type="submit"
        class="btn btn-primary">
        สร้าง
      </button>
    </div>
  </Modal>
</template>

<script>
import _ from 'lodash';
import { createCollection } from '@/collection/api';
import { loadTaxonomyByKey } from '@/taxonomy/api';
import { loadPrograms } from '@/program/api';
import FormGroup from '@/components/FormGroup.vue';
import Modal from '@/components/Modal.vue';
import SelectOptionObject from '@/components/SelectOptionObject.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';

export default {
  props: {
    title: {
      type: String,
    },
  },
  components: {
    Modal,
    FormGroup,
    SelectOptionObject,
    MultiSelect,
    EmptyMessage,
  },
  data() {
    return {
      saveCollectionErrorCode: null,
      debounce: null,
      selectedMethod: null,
      programs: [],
      contentCategories: [],
      step: 1,
      saveCollectionState: {
        isSaving: false,
        isSaved: false,
        error: null,
      },
      saveCollectionStateError: {
        key: null,
        title: null,
        contentCategory: null,
      },
      collection: {
        title: null,
        key: null,
        type: undefined,
        program: null,
        programId: null,
        contentCategory: null,
        contentCategoryId: null,
      },
      collectionType: [
        { id: 1, title: 'เพลย์ลิสต์', value: 'video' },
        { id: 2, title: 'ชุดเพลย์ลิสต์', value: 'playlist' },
        { id: 3, title: 'ชุดตอน', value: 'episode' },
        { id: 4, title: 'ชุดรายการ', value: 'program' },
      ],
      tagOptions: [],
      tagValue: [],
    };
  },
  computed: {
    baseURL() {
      const env = process.env.NODE_ENV;
      if (env === 'production') {
        return 'https://altv.tv';
      }
      return process.env.VUE_APP_BASEURL || window.location.origin;
    },
    type() {
      return this.$route.query.type;
    },
    showDuplicateKeyError() {
      return this.saveCollectionErrorCode && this.isKeyDuplicated();
    },
  },
  created() {
    this.updateCollectionType();
    this.load();
    this.loadPrograms();
    this.loadContentCategories();
  },
  methods: {
    async loadPrograms(filter) {
      const response = await loadPrograms(filter);
      this.programs = response.data.data;
    },
    async loadContentCategories() {
      const response = await loadTaxonomyByKey('content-category');
      this.contentCategories = response.data.data;
    },
    hasContentCategory() {
      const category = _.get(this.collection, 'program.contentCategoryId', null);
      if (category) return true;
      return false;
    },
    onSelectedProgram(program) {
      if (program) {
        this.collection.program = program;
        this.collection.programId = program.id;
        this.collection.contentCategory = _.get(program, 'contentCategory', this.collection.contentCategory);
        this.collection.contentCategoryId = _.get(program, 'contentCategoryId', this.collection.contentCategoryId);
      } else {
        this.collection.program = null;
        this.collection.programId = null;
        this.collection.contentCategory = null;
        this.collection.contentCategoryId = null;
      }
    },
    onSelectedContentCategory(category) {
      if (category) {
        this.collection.contentCategory = category;
        this.collection.contentCategoryId = category.id;
      }
    },
    debounceSearch(event) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.load(event);
      }, 500);
    },
    async load(filter) {
      const response = await loadTaxonomyByKey('tag', filter);
      this.tagOptions = response.data.data;
    },
    isFormValid() {
      if (this.collection.type === 'video' && !this.collection.contentCategoryId) {
        return false;
      }
      return this.collection.title && (this.collection.key && this.isEnglishOnly(this.collection.key));
    },
    checkRequired(event, fieldName, errorMessage) {
      if (!event.target.value) {
        _.set(
          this.saveCollectionStateError,
          fieldName,
          errorMessage,
        );
      } else {
        _.set(
          this.saveCollectionStateError,
          fieldName,
          null,
        );
      }

      if (fieldName === 'key') {
        if (!this.isEnglishOnly(event.target.value)) {
          _.set(
            this.saveCollectionStateError,
            fieldName,
            'ชื่อคีย์จะต้องเป็นภาษาอังกฤษเท่านั้น',
          );
        } else {
          _.set(
            this.saveCollectionStateError,
            'key',
            null,
          );
        }
      }
    },
    getCollectionTitle(collection) {
      const collectionType = _.get(collection, 'type', 'video').toLowerCase();
      switch (collectionType) {
        case 'video':
          return 'เพลย์ลิสต์';
        case 'playlist':
          return 'ชุดเพลย์ลิสต์';
        case 'episode':
          return 'ชุดตอน';
        case 'program':
          return 'ชุดรายการ';
        default:
          return 'คอลเล็คชั่น';
      }
    },
    isEnglishOnly(string) {
      return /^[a-zA-Z0-9-]+$/.test(string);
    },
    updateField(event, key) {
      this.collection[key] = event.target.value;
    },
    updateCollectionType() {
      if (this.type) {
        this.collection.type = this.type;
      }
    },
    getModalTitle() {
      if (this.step === 1) {
        return `เลือกประเภท${this.title}`;
      }
      return `สร้าง${this.title}`;
    },
    onSelectCollectionType(collectionType) {
      this.collection.type = collectionType.value;
    },
    addTag(tag) {
      this.tagValue = tag;
    },
    gotoNextStep() {
      this.step = 2;
    },
    selectMethod(type) {
      this.selectedMethod = type;
    },
    isSelected(type) {
      return type === this.selectedMethod;
    },
    onCloseModal() {
      this.$emit('modalClose');
    },
    isKeyDuplicated() {
      return _.find(this.saveCollectionState.error, { validation: 'duplicated key' });
    },
    async onSubmit() {
      this.saveCollectionErrorCode = null;
      this.saveCollectionState.isSaving = true;
      const payload = {
        isDynamic: this.selectedMethod === 'dynamic',
        tags: this.tagValue,
        itemIds: [],
        sortBy: 'createdAt',
        status: 'draft',
        ...this.collection,
      };
      try {
        const response = await createCollection(payload);
        this.saveCollectionState.isSaved = true;
        this.saveCollectionState.isSaving = false;
        this.$emit('saved', response.data.id);
      } catch (error) {
        this.step = 3;
        this.saveCollectionErrorCode = error.response.status;
        this.saveCollectionState.error = _.get(error, 'response.data.errors', []);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.box-container {
  display: flex;
  flex-direction: row;
  .box {
    margin: $spacer;
    flex: 1;
    display: flex;
    // flex-direction: column;
    align-items: center;
    // justify-content: center;
    border: solid 1px $gray-400;
    align-items: center;
    border-radius: 4px;
    padding: $spacer * 2;
    cursor: pointer;

    &.selected {
      border: solid 2px $gray-700;
      font-weight: bolder;
    }

    .box-content {
      .header {
        color: $gray-700;
      }
    }
  }
}

.modal-body {
  &.no-overflow {
    overflow-y: unset !important;
  }
}
</style>
